<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      :image-url="heroImage"
      :loading-image="heroImageLoading"
      alt="Jóvenes Bochófilos"
      :title="heroTitle"
      :subtitle="heroSubtitle"
    />

    <!-- Listado de jóvenes promesas -->
    <section
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:pt-24 lg:pb-0 sm:py-12"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="futuro" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Futuro</span>
      </div>

      <!-- Stats -->
      <!-- <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10">
        <RevealOnScroll :delay="0">
          <StatCard :value="200" label="Participantes" icon="location" />
        </RevealOnScroll>
        <RevealOnScroll :delay="100">
          <StatCard :value="20" label="Asociaciones" icon="location" />
        </RevealOnScroll>
        <RevealOnScroll :delay="200">
          <StatCard :value="15" label="Torneos" icon="location" />
        </RevealOnScroll>
      </div> -->

      <div v-if="loading" class="py-12 text-center text-bochas-text-muted">
        Cargando jóvenes...
      </div>
      <p v-else-if="error" class="py-12 text-center text-bochas-rojo">
        {{ error }}
      </p>
      <p
        v-else-if="jóvenes.length === 0"
        class="py-12 text-center text-bochas-text-muted"
      >
        Aún no hay Jóvenes Bochófilos publicados.
      </p>

      <!-- Grid de cards -->
      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14"
      >
        <RevealOnScroll
          v-for="(joven, index) in paginatedJovenes"
          :key="joven.id"
          as="article"
          :delay="(index % 3) * 100"
          class="listing-motion-card card-fbochas overflow-hidden flex flex-col active:scale-[0.98]"
        >
          <router-link :to="`/futuro/${joven.id}`" class="flex flex-col h-full">
            <!-- Imagen -->
            <div class="h-[300px] lg:h-[250px] overflow-hidden bg-bochas-bg">
              <img
                v-if="joven.photo_url"
                :src="joven.photo_url"
                :alt="joven.name"
                class="listing-motion-image w-full h-full object-cover"
                loading="lazy"
              />
              <div
                v-else
                class="flex h-full w-full items-center justify-center bg-degradado-bochas text-5xl font-bold text-white"
              >
                {{ initials(joven.name) }}
              </div>
            </div>

            <!-- Contenido -->
            <div class="p-5 sm:p-4 flex-1 flex flex-col">
              <h3
                class="text-lg sm:text-2xl font-medium text-white mb-3 leading-tight"
              >
                {{ joven.name }}
              </h3>
              <div class="space-y-1.5 text-base font-normal text-white">
                <div v-if="joven.association" class="flex items-center gap-2">
                  <IconFbochas name="location" :size="14" color="white" />
                  <span>{{ joven.association }}</span>
                </div>
                <div
                  v-if="
                    joven.careerStartDate ||
                    joven.careerEndDate ||
                    joven.yearsPlaying > 0
                  "
                  class="flex items-center gap-2"
                >
                  <IconFbochas name="reloj" :size="14" color="white" />
                  <span>{{
                    careerPeriod(joven, `${joven.yearsPlaying} años jugando`)
                  }}</span>
                </div>
              </div>
            </div>
          </router-link>
        </RevealOnScroll>
      </div>

      <!-- Paginación -->
      <Paginación
        class="py-14"
        :current-page="currentPage"
        :total-items="jóvenes.length"
        :per-page="perPage"
        @change="onPageChange"
      />

      <!-- CTA -->
      <RevealOnScroll>
        <CtaCard
          title="¿Conocés a un joven bochófilo con talento?"
          description="Nos encantaría conocerlo. Cada historia enriquece nuestra comunidad."
        >
          <a
            href="https://wa.me/5493534252102"
            target="_blank"
            rel="noopener noreferrer"
            class="motion-action inline-block bg-bochas-azul hover:bg-bochas-accent/80 text-white font-semibold px-6 py-3 rounded-full w-full sm:w-auto text-center"
          >
            Contactar a Sergio "Chino" Palacio
          </a>
        </CtaCard>
      </RevealOnScroll>

      <!-- Volver -->
      <div class="py-24">
        <VolverButton />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { useListingState } from "@/composables/useListingState";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import StatCard from "@/components/Common/StatCard.vue";
import Paginación from "@/components/Common/Paginacion.vue";
import CtaCard from "@/components/Common/CtaCard.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { obtenerSiteMediaMap } from "@/services/siteMedia/siteMediaService";
import { listarJovenes } from "@/services/youngPlayers/youngPlayersService";
import { careerPeriod } from "@/utils/playerCareer";
import { getSiteMediaFallback } from "@/data/siteMediaFallbacks";

const heroImage = ref(getSiteMediaFallback("futuro.hero"));
const heroImageLoading = ref(true);
const heroTitle = ref("");
const heroSubtitle = ref("");

onMounted(async () => {
  try {
    const media = await obtenerSiteMediaMap();
    const hero = media["futuro.hero"];
    heroImage.value = hero?.imageUrl || getSiteMediaFallback("futuro.hero");
    heroTitle.value = hero?.title || "Jóvenes Bochófilos";
    heroSubtitle.value =
      hero?.subtitle ||
      "Descubrí a los talentos emergentes del deporte cordobés";
  } catch (_) {
    heroImage.value = getSiteMediaFallback("futuro.hero");
    heroTitle.value = "Jóvenes Bochófilos";
    heroSubtitle.value =
      "Descubrí a los talentos emergentes del deporte cordobés";
  } finally {
    heroImageLoading.value = false;
  }
});

const jóvenes = ref([]);
const loading = ref(true);
const error = ref("");

// Paginación
const perPage = 6;
const { currentPage } = useListingState("futuro", { currentPage: 1 });
const paginatedJovenes = computed(() =>
  jóvenes.value.slice(
    (currentPage.value - 1) * perPage,
    currentPage.value * perPage,
  ),
);

function initials(name) {
  return String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

onMounted(async () => {
  try {
    const data = await listarJovenes();
    jóvenes.value = data.items || [];
  } catch (_) {
    error.value = "No se pudieron cargar los jóvenes.";
  } finally {
    loading.value = false;
  }
});

function onPageChange(page) {
  currentPage.value = page;
}
</script>
